import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtMoney, fmtDate, formatErr } from "../api";
import TopNav from "../components/TopNav";
import StatusBadge from "../components/StatusBadge";
import { Trash2, UserPlus, Check, Copy, ExternalLink, Pencil, Save, Link2, RefreshCw, MessageSquare, X, Plus, CalendarDays, RefreshCcw, AlertTriangle, Clock, AlertCircle, Ban, Minus } from "lucide-react";
import InvitationsPanel from "../components/InvitationsPanel";
import Comments from "../components/Comments";

function BroadcastBox({ groupId }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setBusy(true); setMsg("");
    try {
      await api.post("/admin/broadcast", { title, body, group_id: groupId });
      setMsg("Notification sent to all group members.");
      setTitle(""); setBody("");
    } catch (err) {
      setMsg(formatErr(err?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const [reminderNote, setReminderNote] = useState("");
  const [reminderMsg, setReminderMsg] = useState("");
  const [reminderBusy, setReminderBusy] = useState(false);
  const sendReminder = async (e) => {
    e.preventDefault();
    setReminderBusy(true); setReminderMsg("");
    try {
      const r = await api.post(`/admin/groups/${groupId}/payment-reminder`, { custom_message: reminderNote || null });
      setReminderMsg(r.data.message);
      setReminderNote("");
    } catch (err) {
      setReminderMsg(formatErr(err?.response?.data?.detail) || "Failed to send reminder.");
    } finally { setReminderBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* Payment Reminder */}
      <div className="card-tactile p-6" data-testid="reminder-form">
        <h3 className="font-display text-xl mb-1">💳 Payment reminder</h3>
        <p className="text-xs mb-4" style={{color:"var(--muted)"}}>
          Sends a personalised payment reminder to every member who <strong>hasn't paid yet</strong> for the current active cycle.
          Members who have already submitted or been approved are automatically skipped.
        </p>
        <form onSubmit={sendReminder}>
          <input
            value={reminderNote}
            onChange={e=>setReminderNote(e.target.value)}
            placeholder="Optional extra note to include in the email (e.g. 'Deadline is Friday!')"
            className="w-full border rounded px-3 py-2 bg-white text-sm mb-3"
          />
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium" style={{color: reminderMsg.startsWith("Reminder sent") ? "var(--primary)" : "#b91c1c"}}>{reminderMsg}</div>
            <button disabled={reminderBusy} className="btn-primary text-sm" data-testid="reminder-send">
              {reminderBusy ? "Sending…" : "Send reminder to unpaid members"}
            </button>
          </div>
        </form>
      </div>

      {/* General Broadcast */}
      <form onSubmit={send} className="card-tactile p-6" data-testid="broadcast-form">
        <h3 className="font-display text-xl mb-3">📢 Broadcast notification</h3>
        <p className="text-xs mb-4" style={{color:"var(--muted)"}}>Sends a custom in-app notification + email to <strong>every</strong> active member of this group.</p>
        <div className="grid md:grid-cols-3 gap-3">
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Title"
            className="border rounded px-3 py-2 bg-white md:col-span-1" data-testid="broadcast-title" />
          <input value={body} onChange={e=>setBody(e.target.value)} placeholder="Message body"
            className="border rounded px-3 py-2 bg-white md:col-span-2" data-testid="broadcast-body" />
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm" style={{color:"var(--primary)"}} data-testid="broadcast-msg">{msg}</div>
          <button disabled={busy} className="btn-primary text-sm" data-testid="broadcast-send">
            {busy ? "Sending…" : "Send to all members"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminGroupDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [addEmail, setAddEmail] = useState("");
  const [addPos, setAddPos] = useState("");
  const [err, setErr] = useState("");
  const [addMode, setAddMode] = useState("existing"); // "existing" | "new"
  const [newMemberForm, setNewMemberForm] = useState({ name: "", email: "", phone: "", payout_position: "", use_alias: false, display_name: "", visibility_preference: "visible" });
  const [newMemberBusy, setNewMemberBusy] = useState(false);
  const [tab, setTab] = useState("members");
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [joinToken, setJoinToken] = useState("");
  const [joinCopied, setJoinCopied] = useState(false);
  const [joinRegen, setJoinRegen] = useState(false);
  const [joinLinkLoading, setJoinLinkLoading] = useState(false);
  const [joinLinkErr, setJoinLinkErr] = useState("");
  const [messages, setMessages] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [editingPos, setEditingPos] = useState({});
  const [addSlotTarget, setAddSlotTarget] = useState(null); // { id, email, name }
  const [addSlotPos, setAddSlotPos] = useState("");
  const [addSlotErr, setAddSlotErr] = useState("");
  const [editingDueDate, setEditingDueDate] = useState({}); // { cycle_id: draft_date }
  const [reconciling, setReconciling] = useState(false);
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState("");
  const [recalcConfirm, setRecalcConfirm] = useState(false);
  const [payoutBusy, setPayoutBusy] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [addSlotBusy, setAddSlotBusy] = useState(false);
  const [posBusy, setPosBusy] = useState(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerUser, setLedgerUser] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerEdit, setLedgerEdit] = useState(null);
  const [ledgerSaving, setLedgerSaving] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null); // { id, subject, from_user_name }
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);

  const load = async () => {
    const [d, u] = await Promise.all([
      api.get(`/admin/groups/${id}`).then(r=>r.data),
      api.get("/admin/users").then(r=>r.data),
    ]);
    // also fetch full statuses via the detail endpoint
    const detail = await api.get(`/groups/${id}/detail`).then(r=>r.data);
    setData({ ...d, statuses: detail.statuses, cycles: detail.cycles, member_payment_statuses: detail.member_payment_statuses || {}, active_cycle_no: detail.active_cycle_no ?? null });
    setUsers(u);
  };
  const loadMessages = () =>
    api.get(`/admin/groups/${id}/member-messages`).then(r => setMessages(r.data)).catch(()=>{});

  const loadJoinLink = async () => {
    setJoinLinkLoading(true); setJoinLinkErr("");
    try {
      const r = await api.get(`/admin/groups/${id}/join-link`);
      setJoinToken(r.data.join_token);
    } catch (e) {
      setJoinLinkErr(formatErr(e?.response?.data?.detail) || "Could not load link");
    } finally { setJoinLinkLoading(false); }
  };

  useEffect(() => { load(); loadJoinLink(); loadMessages(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data?.group) setEditData({ ...data.group }); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k, v) => setEditData(prev => ({ ...prev, [k]: v }));

  const saveGroup = async () => {
    setSaving(true); setSaveMsg("");
    try {
      await api.patch(`/admin/groups/${id}`, editData);
      setSaveMsg("Saved!");
      load();
    } catch (e) { setSaveMsg(formatErr(e?.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const runRecalculate = async () => {
    setRecalcBusy(true); setRecalcMsg(""); setRecalcConfirm(false);
    try {
      const payload = {};
      if (editData.start_date) payload.start_date = editData.start_date;
      if (editData.due_day) payload.due_day = Number(editData.due_day);
      const { data: res } = await api.post(`/admin/groups/${id}/recalculate-dates`, payload);
      setRecalcMsg(res.message || `Updated ${res.updated} cycle(s).`);
      load();
    } catch (e) { setRecalcMsg(formatErr(e?.response?.data?.detail) || "Recalculation failed."); }
    finally { setRecalcBusy(false); }
  };

  if (!data) return <div className="min-h-screen bg-app"><TopNav /><div className="page-main">Loading...</div></div>;

  const { group, members, cycles, statuses, member_payment_statuses = {}, active_cycle_no } = data;

  // ── Per-member payment status chip ──
  const PAYMENT_CHIP = {
    Submitted:        { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe", Icon: Clock,       label: "Verifying" },
    Paid:             { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0", Icon: Check,       label: "Paid" },
    Payout_Completed: { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0", Icon: Check,       label: "Paid" },
    Due:              { bg: "#fef3c7", color: "#b45309", border: "#fde68a", Icon: AlertCircle, label: "Due" },
    Overdue:          { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca", Icon: Ban,         label: "Overdue" },
    Rejected:         { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca", Icon: Ban,         label: "Rejected" },
    Not_Due:          { bg: "#f1f5f9", color: "#94a3b8", border: "#e2e8f0", Icon: Minus,       label: "Upcoming" },
  };
  const memberPayChip = (userId) => {
    const st = member_payment_statuses[userId];
    const cfg = PAYMENT_CHIP[st];
    if (!cfg) return null;
    const { bg, color, border, Icon, label } = cfg;
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
        style={{background:bg, color, border:`1px solid ${border}`}}>
        <Icon size={10}/>{label}
      </span>
    );
  };
  const memberIds = new Set(members.map(m=>m.user_id));
  const newUsers = users.filter(u => u.role === "member" && !memberIds.has(u.id));
  const userMap = Object.fromEntries(users.map(u => [u.id, u]));

  // Group members by user_id so multi-slot members appear as one row
  const membersByUser = {};
  for (const m of [...members].sort((a,b)=>a.payout_position-b.payout_position)) {
    if (!membersByUser[m.user_id]) membersByUser[m.user_id] = { info: m, slots: [] };
    membersByUser[m.user_id].slots.push(m);
  }
  const groupedMembers = Object.values(membersByUser).sort((a,b)=>a.slots[0].payout_position-b.slots[0].payout_position);

  const copyText = (text) => navigator.clipboard?.writeText(text);

  const runReconcile = async () => {
    if (!window.confirm("This will clear stale payout assignments from cycles where the slot no longer exists. Continue?")) return;
    setReconciling(true);
    try {
      const r = await api.post("/admin/reconcile-cycle-payouts");
      alert(`Done. ${r.data.cycles_cleared} stale assignment${r.data.cycles_cleared !== 1 ? "s" : ""} cleared.`);
      load();
    } catch (e) { alert(formatErr(e?.response?.data?.detail)); }
    finally { setReconciling(false); }
  };

  const addMember = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post(`/admin/groups/${id}/members`, { email: addEmail, payout_position: addPos ? Number(addPos) : null });
      setAddEmail(""); setAddPos(""); load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail)); }
  };

  const submitNewMember = async (e) => {
    e.preventDefault(); setErr(""); setNewMemberBusy(true);
    try {
      await api.post("/admin/users/provision", {
        name: newMemberForm.name,
        email: newMemberForm.email,
        phone: newMemberForm.phone,
        group_id: id,
        payout_position: newMemberForm.payout_position ? Number(newMemberForm.payout_position) : null,
        use_alias: newMemberForm.use_alias,
        display_name: newMemberForm.display_name,
        visibility_preference: newMemberForm.visibility_preference,
      });
      setNewMemberForm({ name: "", email: "", phone: "", payout_position: "", use_alias: false, display_name: "", visibility_preference: "visible" });
      setAddMode("existing");
      load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail)); }
    finally { setNewMemberBusy(false); }
  };

  const startEditPos = (mid, current) =>
    setEditingPos(prev => ({ ...prev, [mid]: String(current) }));

  const cancelEditPos = (mid) =>
    setEditingPos(prev => { const n = { ...prev }; delete n[mid]; return n; });

  const savePosition = async (mid) => {
    const val = parseInt(editingPos[mid], 10);
    if (!val || val < 1 || posBusy === mid) return;
    setPosBusy(mid);
    try {
      await api.patch(`/admin/groups/${id}/members/${mid}`, { payout_position: val });
      cancelEditPos(mid); load();
    } catch (e) { alert(formatErr(e?.response?.data?.detail)); }
    finally { setPosBusy(null); }
  };

  const remove = (mid, name) => { setRemoveTarget({ uid: mid, name }); setRemoveReason(""); };

  const confirmRemove = async () => {
    if (removeBusy) return; setRemoveBusy(true);
    const qs = removeReason ? `?reason=${encodeURIComponent(removeReason)}` : "";
    try {
      await api.delete(`/admin/groups/${id}/members/${removeTarget.uid}${qs}`);
      setRemoveTarget(null); load();
    } catch (e) { alert(formatErr(e?.response?.data?.detail)); }
    finally { setRemoveBusy(false); }
  };

  const confirmPayout = async (cycleNo) => {
    if (payoutBusy !== null) return;
    if (!window.confirm(`Confirm payout for cycle ${cycleNo}?`)) return;
    setPayoutBusy(cycleNo);
    try { await api.post(`/admin/payouts/${id}/${cycleNo}/confirm`); load(); }
    catch (e) { alert(formatErr(e?.response?.data?.detail)); }
    finally { setPayoutBusy(null); }
  };

  const addSlot = async (e) => {
    e.preventDefault(); if (addSlotBusy) return; setAddSlotErr(""); setAddSlotBusy(true);
    try {
      await api.post(`/admin/groups/${id}/members`, { email: addSlotTarget.email, payout_position: Number(addSlotPos) });
      setAddSlotTarget(null); setAddSlotPos(""); load();
    } catch (e) { setAddSlotErr(formatErr(e?.response?.data?.detail)); }
    finally { setAddSlotBusy(false); }
  };

  const saveDueDate = async (cycleId, draftDate) => {
    try {
      await api.patch(`/admin/groups/${id}/cycles/${cycleId}`, { due_date: draftDate });
      setEditingDueDate(prev => { const n = {...prev}; delete n[cycleId]; return n; });
      load();
    } catch (e) { alert(formatErr(e?.response?.data?.detail)); }
  };

  const openLedger = async (userId) => {
    setLedgerUser(userId);
    setLedgerOpen(true);
    setLedgerLoading(true);
    setLedgerData([]);
    try {
      const { data } = await api.get(`/admin/ledger/${id}/${userId}`);
      setLedgerData(data.ledger || []);
    } catch (e) {
      alert(formatErr(e?.response?.data?.detail) || "Failed to load ledger");
    } finally { setLedgerLoading(false); }
  };

  const startLedgerEdit = (entry) => {
    setLedgerEdit({
      cycle_no: entry.cycle_no,
      status: entry.status,
      paid_amount: entry.paid_amount || "",
      note: entry.manual_override_note || "",
    });
  };

  const saveLedgerEdit = async () => {
    if (!ledgerEdit || ledgerSaving) return;
    setLedgerSaving(true);
    try {
      await api.post("/admin/ledger/manual-update", {
        group_id: id,
        user_id: ledgerUser,
        cycle_no: ledgerEdit.cycle_no,
        status: ledgerEdit.status,
        paid_amount: ledgerEdit.paid_amount ? Number(ledgerEdit.paid_amount) : null,
        note: ledgerEdit.note || null,
      });
      setLedgerEdit(null);
      openLedger(ledgerUser); // reload
      load(); // refresh main view
    } catch (e) {
      alert(formatErr(e?.response?.data?.detail) || "Failed to update ledger");
    } finally { setLedgerSaving(false); }
  };

  const sendReply = async () => {
    if (!replyBody.trim() || replySending) return;
    setReplySending(true);
    try {
      await api.post(`/admin/member-messages/${replyTarget.id}/reply`, { body: replyBody });
      setReplyTarget(null);
      setReplyBody("");
      loadMessages();
    } catch (e) {
      alert(formatErr(e?.response?.data?.detail) || "Failed to send reply");
    } finally { setReplySending(false); }
  };

  // Build status matrix: members x cycles
  const statusMap = {};
  for (const s of statuses) statusMap[`${s.user_id}_${s.cycle_no}`] = s.status;

  return (
    <div className="min-h-screen bg-app">
      <TopNav />
      <main className="page-main">
        <Link to="/admin" className="text-sm" style={{color:"var(--muted)"}} data-testid="back-admin">← Back to admin</Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mt-2 mb-6 sm:mb-8">
          <div className="min-w-0">
            <div className="label-eyebrow">{group.frequency} · {group.total_cycles} cycles</div>
            <h1 className="font-display text-2xl sm:text-4xl mt-1">{group.name}</h1>
            {group.description && <p className="text-sm mt-1" style={{color:"var(--muted)"}}>{group.description}</p>}
          </div>
          <div className="card-tactile p-4 sm:p-5 flex sm:flex-col gap-4 sm:gap-0 sm:min-w-[200px] items-center sm:items-start shrink-0">
            <div>
              <div className="label-eyebrow">Contribution</div>
              <div className="font-display text-2xl sm:text-3xl mt-0.5">{fmtMoney(group.contribution_amount)}</div>
            </div>
            <div>
              <div className="text-xs" style={{color:"var(--muted)"}}>{members.length}/{group.member_limit} members</div>
              {group.whatsapp_invite_link && (
                <a href={group.whatsapp_invite_link} target="_blank" rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded"
                  style={{background:"#25D36615", color:"#128C7E", border:"1px solid #25D36630"}}>
                  <ExternalLink size={11}/> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-1 border-b mb-6 overflow-x-auto scrollbar-none" style={{borderColor:"var(--border)"}}>
          {["members","messages","invitations","ledger","payouts","comments","settings"].map(k => (
            <button key={k} onClick={()=>setTab(k)}
              className={`px-4 py-2.5 text-sm border-b-2 -mb-px capitalize whitespace-nowrap shrink-0 ${tab===k?"font-semibold":"opacity-60"}`}
              style={{borderColor: tab===k?"var(--primary)":"transparent", color: tab===k?"var(--primary)":"var(--text)"}}
              data-testid={`gtab-${k}`}>{k}</button>
          ))}
        </div>

        {tab === "messages" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg">Member messages ({messages.length})</h3>
              <button onClick={loadMessages} className="text-xs" style={{color:"var(--muted)"}}>Refresh</button>
            </div>
            {messages.length === 0 && (
              <div className="card-tactile p-8 text-center text-sm" style={{color:"var(--muted)"}}>No messages yet.</div>
            )}
            {messages.map(m => (
              <div key={m.id} className={`card-tactile p-4 ${!m.read ? "border-l-4" : ""}`}
                style={!m.read ? {borderLeftColor:"var(--primary)"} : {}}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{m.subject}</div>
                    <div className="text-xs mt-0.5" style={{color:"var(--muted)"}}>
                      {m.from_user_name} · {m.from_user_email} · {fmtDate(m.created_at)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!m.read && (
                      <button onClick={async()=>{ await api.patch(`/admin/member-messages/${m.id}/read`); loadMessages(); }}
                        className="text-xs shrink-0 px-2 py-1 rounded" style={{background:"var(--surface)",color:"var(--primary)"}}>
                        Mark read
                      </button>
                    )}
                    <button onClick={()=>{ setReplyTarget({ id: m.id, subject: m.subject, from_user_name: m.from_user_name }); setReplyBody(""); }}
                      className="text-xs shrink-0 px-2 py-1 rounded" style={{background:"var(--primary)",color:"#fff"}}>
                      Reply
                    </button>
                  </div>
                </div>
                <div className="mt-2 text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "members" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 card-tactile overflow-hidden" data-testid="group-members-table">
              {active_cycle_no != null && (
                <div className="px-4 pt-3 pb-1 flex items-center gap-1.5 text-xs" style={{color:"var(--muted)"}}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60 shrink-0"/>
                  Month {active_cycle_no} payment status
                </div>
              )}
              {/* Mobile member cards */}
              <div className="mobile-list-card divide-y" style={{borderColor:"var(--border)"}}>
                {groupedMembers.map(({ info: m, slots }) => {
                  const u = userMap[m.user_id];
                  const hasBank = u?.bank_name && u?.bank_account_number;
                  return (
                    <div key={m.user_id} className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-sm truncate">{m.user_name}</div>
                            {memberPayChip(m.user_id)}
                          </div>
                          <div className="text-xs truncate" style={{color:"var(--muted)"}}>{m.user_email}</div>
                          {hasBank ? (
                            <div className="text-xs mt-1 flex items-center gap-1" style={{color:"var(--muted)"}}>
                              <span>{u.bank_name} · {u.bank_account_number}</span>
                              <button onClick={()=>copyText(u.bank_account_number)} title="Copy" className="opacity-50 hover:opacity-100"><Copy size={10}/></button>
                            </div>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded mt-1 inline-block" style={{background:"#fee2e2",color:"#991b1b"}}>Bank not set</span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={()=>openLedger(m.user_id)}
                            className="shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1"
                            style={{background:"var(--surface)",color:"var(--muted)"}} title="Manage ledger">
                            <RefreshCcw size={11}/> Ledger
                          </button>
                          <button
                            onClick={()=>{ setAddSlotTarget({id:m.user_id,email:m.user_email,name:m.user_name}); setAddSlotPos(""); setAddSlotErr(""); }}
                            className="shrink-0 text-xs font-semibold px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1"
                            style={{background:"var(--primary)15",color:"var(--primary)"}}>
                            <Plus size={11}/> Add slot
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1.5 mb-2">
                        {slots.map(s => {
                          const pc = cycles.find(c=>c.cycle_no===s.payout_position);
                          return (
                            <div key={s.id} className="flex items-center gap-2 flex-wrap">
                              {editingPos[s.id] !== undefined ? (
                                <div className="flex items-center gap-1">
                                  <input type="number" min={1} value={editingPos[s.id]}
                                    onChange={e=>setEditingPos(p=>({...p,[s.id]:e.target.value}))}
                                    onKeyDown={e=>{ if(e.key==="Enter") savePosition(s.id); if(e.key==="Escape") cancelEditPos(s.id); }}
                                    className="w-14 border rounded px-1.5 py-1 text-sm text-center font-display" autoFocus />
                                  <button onClick={()=>savePosition(s.id)} disabled={posBusy === s.id} className="text-xs font-semibold px-1.5 py-1 rounded" style={{background:"var(--primary)",color:"#fff"}}>{posBusy === s.id ? "..." : "✓"}</button>
                                  <button onClick={()=>cancelEditPos(s.id)} disabled={posBusy === s.id} className="text-xs px-1.5 py-1 rounded" style={{background:"var(--surface)"}}>✕</button>
                                </div>
                              ) : (
                                <button onClick={()=>startEditPos(s.id,s.payout_position)}
                                  className="badge s-Payout_Eligible inline-flex items-center gap-1 cursor-pointer"
                                  title="Click to change slot number">
                                  #{s.payout_position} <Pencil size={9} className="opacity-60"/>
                                </button>
                              )}
                              {pc && <span className="text-xs" style={{color:"var(--muted)"}}>{fmtDate(pc.due_date)}</span>}
                              <button onClick={()=>remove(s.id,`${m.user_name} slot #${s.payout_position}`)} className="text-red-600 ml-auto opacity-60 hover:opacity-100" data-testid={`remove-${s.id}`} title="Remove this slot"><Trash2 size={12}/></button>
                            </div>
                          );
                        })}
                      </div>
                      <div className="text-xs" style={{color:"var(--muted)"}}>Joined {fmtDate(m.joined_at)} · {slots.length} slot{slots.length>1?"s":""}</div>
                    </div>
                  );
                })}
                {members.length===0 && <div className="px-4 py-10 text-center text-sm" style={{color:"var(--muted)"}}>No members yet.</div>}
              </div>
              {/* Desktop table */}
              <table className="desktop-table w-full text-sm">
                <thead className="bg-white/50"><tr className="text-left">
                  <th className="px-4 py-3 label-eyebrow">Member</th>
                  <th className="px-4 py-3 label-eyebrow">Slots &amp; payout dates</th>
                  <th className="px-4 py-3 label-eyebrow">Bank details</th>
                  <th className="px-4 py-3 label-eyebrow">Joined</th>
                </tr></thead>
                <tbody>
                  {groupedMembers.map(({ info: m, slots }) => {
                    const u = userMap[m.user_id];
                    const hasBank = u?.bank_name && u?.bank_account_number;
                    return (
                    <tr key={m.user_id} className="border-t align-top" style={{borderColor:"var(--border)"}}>
                      <td className="px-4 py-3">
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          {m.user_name}{memberPayChip(m.user_id)}
                        </div>
                        <div className="text-xs mt-0.5" style={{color:"var(--muted)"}}>{m.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1.5">
                          {slots.map(s => {
                            const pc = cycles.find(c=>c.cycle_no===s.payout_position);
                            return (
                              <div key={s.id} className="flex items-center gap-2">
                                {editingPos[s.id] !== undefined ? (
                                  <div className="flex items-center gap-1">
                                    <input type="number" min={1} value={editingPos[s.id]}
                                      onChange={e=>setEditingPos(p=>({...p,[s.id]:e.target.value}))}
                                      onKeyDown={e=>{ if(e.key==="Enter") savePosition(s.id); if(e.key==="Escape") cancelEditPos(s.id); }}
                                      className="w-16 border rounded px-2 py-1 text-sm text-center font-display" autoFocus />
                                    <button onClick={()=>savePosition(s.id)} disabled={posBusy === s.id} className="text-xs font-semibold px-1.5 py-1 rounded" style={{background:"var(--primary)",color:"#fff"}} title="Save">{posBusy === s.id ? "..." : "✓"}</button>
                                    <button onClick={()=>cancelEditPos(s.id)} disabled={posBusy === s.id} className="text-xs px-1.5 py-1 rounded" style={{background:"var(--surface)"}} title="Cancel">✕</button>
                                  </div>
                                ) : (
                                  <button onClick={()=>startEditPos(s.id,s.payout_position)}
                                    className="font-display inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs"
                                    style={{borderColor:"var(--border)",color:"var(--text)"}}
                                    title="Change slot number">
                                    <Pencil size={10} className="opacity-60"/> Slot #{s.payout_position}
                                  </button>
                                )}
                                {pc && <span className="text-xs" style={{color:"var(--muted)"}}>{fmtDate(pc.due_date)}</span>}
                                <button onClick={()=>remove(s.id,`${m.user_name} slot #${s.payout_position}`)} className="text-red-600 opacity-40 hover:opacity-100 ml-1" title="Remove this slot" data-testid={`remove-${s.id}`}><Trash2 size={12}/></button>
                              </div>
                            );
                          })}
                          <div className="flex gap-2 mt-1">
                            <button
                              onClick={()=>openLedger(m.user_id)}
                              className="text-xs font-semibold inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                              style={{color:"var(--muted)"}} title="Manage ledger">
                              <RefreshCcw size={11}/> Ledger
                            </button>
                            <button
                              onClick={()=>{ setAddSlotTarget({id:m.user_id,email:m.user_email,name:m.user_name}); setAddSlotPos(""); setAddSlotErr(""); }}
                              className="text-xs font-semibold inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                              style={{color:"var(--primary)"}}>
                              <Plus size={11}/> Add slot
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {hasBank ? (
                          <div className="text-xs">
                            <div className="font-semibold">{u.bank_name}</div>
                            <div className="flex items-center gap-1" style={{color:"var(--muted)"}}>
                              <span>{u.bank_account_number}</span>
                              <button onClick={()=>copyText(u.bank_account_number)} title="Copy" className="opacity-50 hover:opacity-100"><Copy size={10}/></button>
                            </div>
                            <div style={{color:"var(--muted)"}}>{u.bank_account_name}</div>
                          </div>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded" style={{background:"#fee2e2",color:"#991b1b"}}>Not set</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{color:"var(--muted)"}}>{fmtDate(m.joined_at)}</td>
                    </tr>
                    );
                  })}
                  {members.length===0 && <tr><td colSpan={4} className="px-4 py-10 text-center" style={{color:"var(--muted)"}}>No members yet.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="card-tactile p-4 sm:p-5" data-testid="add-member-form">
              <h3 className="font-display text-lg mb-3 flex items-center gap-2"><UserPlus size={16}/> Add member</h3>
              {/* Mode toggle */}
              <div className="flex rounded-lg overflow-hidden border mb-4 text-sm" style={{borderColor:"var(--border)"}}>
                <button type="button" onClick={()=>{ setAddMode("existing"); setErr(""); }}
                  className={`flex-1 py-2 font-semibold transition-colors ${addMode==="existing" ? "text-white" : ""}`}
                  style={{background: addMode==="existing" ? "var(--primary)" : "transparent", color: addMode==="existing" ? "#fff" : "var(--muted)"}}>
                  Existing user
                </button>
                <button type="button" onClick={()=>{ setAddMode("new"); setErr(""); }}
                  className="flex-1 py-2 font-semibold transition-colors"
                  style={{background: addMode==="new" ? "var(--primary)" : "transparent", color: addMode==="new" ? "#fff" : "var(--muted)"}}>
                  New user
                </button>
              </div>

              {addMode === "existing" ? (
                <form onSubmit={addMember}>
                  <div className="mb-3">
                    <label className="form-label">Member</label>
                    <select required value={addEmail} onChange={e=>setAddEmail(e.target.value)} className="form-input" data-testid="add-email">
                      <option value="">— Select existing user —</option>
                      {newUsers.map(u => <option key={u.id} value={u.email}>{u.name} · {u.email}</option>)}
                    </select>
                    {newUsers.length === 0 && <p className="text-xs mt-1" style={{color:"var(--muted)"}}>All registered users are already in this group. Use <b>New user</b> to create one.</p>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Payout slot <span className="font-normal">(leave empty for next available)</span></label>
                    <input type="number" min="1" value={addPos} onChange={e=>setAddPos(e.target.value)} className="form-input" placeholder="e.g. 3" data-testid="add-position"/>
                  </div>
                  {err && <div className="px-3 py-2 rounded-lg text-sm text-red-700 mb-3" style={{background:"#fef2f2"}} data-testid="add-error">{err}</div>}
                  <button className="btn-primary text-sm w-full" data-testid="add-submit">Add to group</button>
                </form>
              ) : (
                <form onSubmit={submitNewMember}>
                  <div className="space-y-3">
                    <div>
                      <label className="form-label">Full name *</label>
                      <input required value={newMemberForm.name} onChange={e=>setNewMemberForm(f=>({...f,name:e.target.value}))} className="form-input" placeholder="e.g. Amaka Osei" />
                    </div>
                    <div>
                      <label className="form-label">Email address *</label>
                      <input required type="email" value={newMemberForm.email} onChange={e=>setNewMemberForm(f=>({...f,email:e.target.value}))} className="form-input" placeholder="amaka@example.com" />
                    </div>
                    <div>
                      <label className="form-label">Phone <span className="font-normal">(optional)</span></label>
                      <input value={newMemberForm.phone} onChange={e=>setNewMemberForm(f=>({...f,phone:e.target.value}))} className="form-input" placeholder="+234..." />
                    </div>
                    <div>
                      <label className="form-label">Payout slot <span className="font-normal">(leave empty for next available)</span></label>
                      <input type="number" min="1" value={newMemberForm.payout_position} onChange={e=>setNewMemberForm(f=>({...f,payout_position:e.target.value}))} className="form-input" placeholder="e.g. 3" />
                    </div>
                    <div className="pt-2 border-t" style={{borderColor:"var(--border)"}}>
                      <div className="text-xs font-semibold mb-2" style={{color:"var(--muted)"}}>Privacy</div>
                      <label className="flex items-start gap-2 cursor-pointer mb-2">
                        <input type="checkbox" checked={newMemberForm.use_alias} onChange={e=>setNewMemberForm(f=>({...f,use_alias:e.target.checked}))} className="mt-0.5 w-4 h-4" />
                        <span className="text-xs"><b>Hide real name</b> — show alias to other members</span>
                      </label>
                      {newMemberForm.use_alias && (
                        <input value={newMemberForm.display_name} onChange={e=>setNewMemberForm(f=>({...f,display_name:e.target.value}))} className="form-input text-sm mb-2" placeholder="Alias / display name" />
                      )}
                      <select value={newMemberForm.visibility_preference} onChange={e=>setNewMemberForm(f=>({...f,visibility_preference:e.target.value}))} className="form-input text-sm bg-white">
                        <option value="visible">Visible to all members</option>
                        <option value="limited">Limited — admins only see details</option>
                        <option value="hidden">Hidden — only admins know they exist</option>
                      </select>
                    </div>
                  </div>
                  {err && <div className="px-3 py-2 rounded-lg text-sm text-red-700 mt-3" style={{background:"#fef2f2"}}>{err}</div>}
                  <p className="text-xs mt-2" style={{color:"var(--muted)"}}>A setup email will be sent so they can create their password.</p>
                  <button disabled={newMemberBusy} className="btn-primary text-sm w-full mt-3">
                    {newMemberBusy ? "Creating…" : "Create & add to group"}
                  </button>
                </form>
              )}
              <p className="text-xs mt-3" style={{color:"var(--muted)"}}>To give an existing member a second payout slot, use the <b>+ Add slot</b> button on their row.</p>
            </div>
          </div>
        )}

        {tab === "invitations" && (
          <div className="space-y-6">
            {/* ── Shareable group join link ── */}
            <div className="card-tactile p-4 sm:p-6">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-display text-lg flex items-center gap-2"><Link2 size={16}/> Shareable group link</h3>
                  <p className="text-xs mt-1" style={{color:"var(--muted)"}}>
                    Share this link with anyone — they sign up with their own email and join directly. No email required from you.
                  </p>
                </div>
              </div>
              {joinLinkLoading && (
                <div className="text-sm" style={{color:"var(--muted)"}}>Loading link…</div>
              )}
              {!joinLinkLoading && joinLinkErr && (
                <div className="space-y-2">
                  <div className="px-3 py-2.5 rounded-lg text-sm" style={{background:"#fef2f2",color:"#b91c1c"}}>{joinLinkErr}</div>
                  <button onClick={loadJoinLink} className="btn-secondary text-sm inline-flex items-center gap-1.5">
                    <RefreshCw size={13}/> Retry
                  </button>
                </div>
              )}
              {!joinLinkLoading && !joinLinkErr && joinToken && (() => {
                const joinUrl = `${window.location.origin}/join/${joinToken}`;
                const copyLink = async () => {
                  try {
                    if (navigator.clipboard && window.isSecureContext) {
                      await navigator.clipboard.writeText(joinUrl);
                    } else {
                      const ta = document.createElement("textarea");
                      ta.value = joinUrl;
                      ta.style.cssText = "position:fixed;top:0;left:0;opacity:0";
                      document.body.appendChild(ta);
                      ta.focus(); ta.select();
                      document.execCommand("copy");
                      document.body.removeChild(ta);
                    }
                    setJoinCopied(true);
                    setTimeout(() => setJoinCopied(false), 2500);
                  } catch { setJoinCopied(false); }
                };
                const shareLink = () => navigator.share({ title: group.name, text: `Join my ajo group — ${group.name}`, url: joinUrl });
                return (
                  <>
                    {/* Selectable URL input — works on mobile tap-to-select */}
                    <div className="flex gap-2 mb-2">
                      <input
                        readOnly
                        value={joinUrl}
                        onFocus={e => e.target.select()}
                        onClick={e => e.target.select()}
                        className="flex-1 px-3 py-2.5 rounded-lg border text-sm font-mono min-w-0"
                        style={{background:"var(--surface)",borderColor:"var(--border)",color:"var(--text)"}}
                      />
                      <button
                        onClick={copyLink}
                        className="px-4 py-2.5 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 shrink-0 transition-colors"
                        style={joinCopied
                          ? {background:"#f0fdf4",color:"#16a34a",borderColor:"#86efac"}
                          : {borderColor:"var(--border)",color:"var(--primary)"}}>
                        {joinCopied ? <><Check size={14}/> Copied!</> : <><Copy size={14}/> Copy</>}
                      </button>
                    </div>
                    {/* Mobile native share — only shown on devices that support it */}
                    {typeof navigator.share === "function" && (
                      <button
                        onClick={shareLink}
                        className="w-full py-2.5 rounded-lg border text-sm font-medium inline-flex items-center justify-center gap-2 mb-2"
                        style={{borderColor:"var(--primary)",color:"var(--primary)"}}>
                        <ExternalLink size={14}/> Share link
                      </button>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <a href={joinUrl} target="_blank" rel="noreferrer"
                        className="text-xs inline-flex items-center gap-1" style={{color:"var(--primary)"}}>
                        <ExternalLink size={12}/> Preview join page
                      </a>
                      <button
                        disabled={joinRegen}
                        onClick={async () => {
                          if (!window.confirm("Regenerate the link? The old link will stop working.")) return;
                          setJoinRegen(true);
                          try {
                            const r = await api.post(`/admin/groups/${id}/regenerate-join-link`);
                            setJoinToken(r.data.join_token);
                          } finally { setJoinRegen(false); }
                        }}
                        className="text-xs inline-flex items-center gap-1" style={{color:"var(--muted)"}}>
                        <RefreshCw size={11}/> {joinRegen ? "Regenerating…" : "Regenerate link"}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* ── Personal email invitations (optional, for tracking) ── */}
            <InvitationsPanel groupId={id} />
          </div>
        )}

        {tab === "comments" && (
          <div className="space-y-6">
            <BroadcastBox groupId={id} />
            <Comments groupId={id} />
          </div>
        )}

        {tab === "ledger" && (
          <div className="card-tactile overflow-x-auto">
            <table className="text-sm" data-testid="ledger-matrix">
              <thead className="bg-white/50">
                <tr className="text-left">
                  <th className="px-3 py-2 label-eyebrow sticky left-0 bg-[var(--surface)]">Member \\ Cycle</th>
                  {cycles.map(c => <th key={c.id} className="px-3 py-2 label-eyebrow text-center">#{c.cycle_no}</th>)}
                </tr>
              </thead>
              <tbody>
                {[...members].sort((a,b)=>a.payout_position-b.payout_position).map(m => (
                  <tr key={m.id} className="border-t" style={{borderColor:"var(--border)"}}>
                    <td className="px-3 py-2 sticky left-0 bg-[var(--surface)] whitespace-nowrap">{m.user_name}</td>
                    {cycles.map(c => (
                      <td key={c.id} className="px-3 py-2 text-center">
                        <StatusBadge status={statusMap[`${m.user_id}_${c.cycle_no}`] || "Not_Due"} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "payouts" && (
          <div className="card-tactile overflow-hidden" data-testid="payouts-table">
            <div className="px-4 py-3 flex items-center justify-between gap-3 border-b" style={{borderColor:"var(--border)"}}>
              <div className="text-xs" style={{color:"var(--muted)"}}>Each row is one monthly cycle. Recipient is the member with a slot at that position.</div>
              <button onClick={runReconcile} disabled={reconciling}
                className="text-xs px-3 py-1.5 rounded-lg font-semibold shrink-0 inline-flex items-center gap-1"
                style={{background:"var(--surface)",border:"1px solid var(--border)",color:"var(--muted)"}}>
                {reconciling ? "Fixing…" : "Fix stale assignments"}
              </button>
            </div>
            {/* Mobile payout cards */}
            <div className="mobile-list-card divide-y" style={{borderColor:"var(--border)"}}>
              {cycles.map(c => {
                // Recipient: the member whose payout_position matches this cycle_no
                const recipient = members.find(m => m.payout_position === c.cycle_no);
                const recUser = recipient ? userMap[recipient.user_id] : null;
                const bankStr = recUser?.bank_account_number
                  ? `${recUser.bank_name} — ${recUser.bank_account_number} (${recUser.bank_account_name})`
                  : null;
                return (
                  <div key={c.id} className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div>
                        <div className="font-semibold text-sm">Cycle #{c.cycle_no}</div>
                        <div className="text-xs mt-0.5 flex items-center gap-1" style={{color:"var(--muted)"}}>
                          {editingDueDate[c.id] !== undefined ? (
                            <>
                              <input type="date" value={editingDueDate[c.id]}
                                onChange={e=>setEditingDueDate(p=>({...p,[c.id]:e.target.value}))}
                                className="border rounded px-1.5 py-0.5 text-xs" />
                              <button onClick={()=>saveDueDate(c.id,editingDueDate[c.id])} className="font-semibold px-1.5 py-0.5 rounded text-xs" style={{background:"var(--primary)",color:"#fff"}}>✓</button>
                              <button onClick={()=>setEditingDueDate(p=>{const n={...p};delete n[c.id];return n;})} className="px-1.5 py-0.5 rounded text-xs" style={{background:"var(--surface)"}} >✕</button>
                            </>
                          ) : (
                            <button onClick={()=>setEditingDueDate(p=>({...p,[c.id]:c.due_date}))} className="inline-flex items-center gap-1 group" title="Edit due date">
                              {fmtDate(c.due_date)} <Pencil size={9} className="opacity-0 group-hover:opacity-60"/>
                            </button>
                          )}
                        </div>
                      </div>
                      <span className={`badge shrink-0 ${c.payout_status==="completed"?"s-Payout_Completed":"s-Payout_Eligible"}`}>{c.payout_status}</span>
                    </div>
                    {recipient && <div className="text-sm font-medium">{recipient.user_name}</div>}
                    {bankStr ? (
                      <div className="flex items-center gap-1 mt-1 text-xs" style={{color:"var(--muted)"}}>
                        <span className="truncate">{recUser.bank_name} · {recUser.bank_account_number}</span>
                        <button onClick={()=>copyText(bankStr)} title="Copy" className="opacity-50 shrink-0"><Copy size={10}/></button>
                      </div>
                    ) : <div className="text-xs mt-1" style={{color:"var(--muted)"}}>No bank on file</div>}
                    {c.payout_status !== "completed" && recipient && (
                      <button onClick={()=>confirmPayout(c.cycle_no)} disabled={payoutBusy !== null} className="btn-primary w-full !py-2.5 text-sm inline-flex items-center justify-center gap-1 mt-3" data-testid={`payout-${c.cycle_no}`}>
                        <Check size={14}/>{payoutBusy === c.cycle_no ? "Confirming..." : "Confirm payout"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Desktop table */}
            <table className="desktop-table w-full text-sm">
              <thead className="bg-white/50"><tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Cycle</th>
                <th className="px-4 py-3 label-eyebrow">Due date</th>
                <th className="px-4 py-3 label-eyebrow">Recipient</th>
                <th className="px-4 py-3 label-eyebrow">Recipient bank</th>
                <th className="px-4 py-3 label-eyebrow">Status</th>
                <th className="px-4 py-3 label-eyebrow"></th>
              </tr></thead>
              <tbody>
                {cycles.map(c => {
                  // Recipient: the member whose payout_position matches this cycle_no
                  const recipient = members.find(m => m.payout_position === c.cycle_no);
                  const recUser = recipient ? userMap[recipient.user_id] : null;
                  const bankStr = recUser?.bank_account_number
                    ? `${recUser.bank_name} — ${recUser.bank_account_number} (${recUser.bank_account_name})`
                    : null;
                  return (
                    <tr key={c.id} className="border-t" style={{borderColor:"var(--border)"}}>
                      <td className="px-4 py-3 font-display">#{c.cycle_no}</td>
                      <td className="px-4 py-3">
                        {editingDueDate[c.id] !== undefined ? (
                          <div className="flex items-center gap-1">
                            <input type="date" value={editingDueDate[c.id]}
                              onChange={e=>setEditingDueDate(p=>({...p,[c.id]:e.target.value}))}
                              className="border rounded px-2 py-1 text-sm" />
                            <button onClick={()=>saveDueDate(c.id,editingDueDate[c.id])} className="text-xs font-semibold px-1.5 py-1 rounded" style={{background:"var(--primary)",color:"#fff"}} title="Save">✓</button>
                            <button onClick={()=>setEditingDueDate(p=>{const n={...p};delete n[c.id];return n;})} className="text-xs px-1.5 py-1 rounded" style={{background:"var(--surface)"}} title="Cancel">✕</button>
                          </div>
                        ) : (
                          <button onClick={()=>setEditingDueDate(p=>({...p,[c.id]:c.due_date}))}
                            className="inline-flex items-center gap-1 group opacity-90 hover:opacity-100" title="Edit due date">
                            {fmtDate(c.due_date)} <Pencil size={10} className="opacity-0 group-hover:opacity-60"/>
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{recipient?.user_name || <span style={{color:"var(--muted)"}}>—</span>}</td>
                      <td className="px-4 py-3">
                        {bankStr ? (
                          <div className="flex items-center gap-1">
                            <div className="text-xs">
                              <div className="font-semibold">{recUser.bank_name}</div>
                              <div style={{color:"var(--muted)"}}>{recUser.bank_account_number} · {recUser.bank_account_name}</div>
                            </div>
                            <button onClick={()=>copyText(bankStr)} title="Copy bank details" className="opacity-40 hover:opacity-100 shrink-0"><Copy size={11}/></button>
                          </div>
                        ) : <span className="text-xs" style={{color:"var(--muted)"}}>—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${c.payout_status==="completed"?"s-Payout_Completed":"s-Payout_Eligible"}`}>{c.payout_status}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {c.payout_status !== "completed" && recipient && (
                          <button onClick={()=>confirmPayout(c.cycle_no)} disabled={payoutBusy !== null} className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`payout-${c.cycle_no}`}>
                            <Check size={12}/>{payoutBusy === c.cycle_no ? "Confirming..." : "Confirm payout"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "settings" && editData && (
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            <div className="card-tactile p-4 sm:p-6 space-y-4">
              <h3 className="font-display text-lg flex items-center gap-2"><Pencil size={15}/> Basic details</h3>
              <div>
                <label className="form-label">Group name</label>
                <input value={editData.name||""} onChange={e=>setField("name",e.target.value)} className="form-input"/>
              </div>
              <div>
                <label className="form-label">Description</label>
                <textarea rows={2} value={editData.description||""} onChange={e=>setField("description",e.target.value)} className="form-input"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Contribution (₦)</label>
                  <input type="number" value={editData.contribution_amount||""} onChange={e=>setField("contribution_amount",Number(e.target.value))} className="form-input"/>
                </div>
                <div>
                  <label className="form-label">Member limit</label>
                  <input type="number" value={editData.member_limit||""} onChange={e=>setField("member_limit",Number(e.target.value))} className="form-input"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Total cycles</label>
                  <input type="number" min={1} value={editData.total_cycles||""} onChange={e=>setField("total_cycles",Number(e.target.value))} className="form-input"/>
                  <p className="text-xs mt-1" style={{color:"var(--muted)"}}>
                    Reducing removes future (unpaid) cycles. Increasing adds new ones automatically.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Frequency</label>
                  <select value={editData.frequency||"monthly"} onChange={e=>setField("frequency",e.target.value)} className="form-input">
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <select value={editData.status||"active"} onChange={e=>setField("status",e.target.value)} className="form-input">
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Due day <span className="font-normal text-xs" style={{color:"var(--muted)"}}>day of month (1–28)</span></label>
                  <input type="number" min={1} max={28} value={editData.due_day||1} onChange={e=>setField("due_day",Number(e.target.value))} className="form-input"/>
                </div>
                <div>
                  <label className="form-label">Due time</label>
                  <input type="time" value={editData.due_time||"23:59"} onChange={e=>setField("due_time",e.target.value)} className="form-input"/>
                </div>
              </div>
              <div>
                <label className="form-label">Group start date</label>
                <input type="date" value={editData.start_date||""} onChange={e=>setField("start_date",e.target.value)} className="form-input"/>
                <p className="text-xs mt-1 px-2 py-1.5 rounded-lg" style={{color:"#92400e", background:"#fef3c7"}}>
                  ⚡ Saving any change to <strong>Start date</strong>, <strong>Due day</strong> or <strong>Frequency</strong> will automatically recalculate all pending cycle due dates — member slots &amp; payout months update instantly.
                </p>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div className="card-tactile p-4 sm:p-6 space-y-4">
                <h3 className="font-display text-lg">Fees &amp; rules</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label">First-payment fee (₦)</label>
                    <input type="number" value={editData.first_payment_fee||0} onChange={e=>setField("first_payment_fee",Number(e.target.value))} className="form-input"/>
                  </div>
                  <div>
                    <label className="form-label">Late fee (₦)</label>
                    <input type="number" value={editData.late_fee_amount||0} onChange={e=>setField("late_fee_amount",Number(e.target.value))} className="form-input"/>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label">Late fee method</label>
                    <select value={editData.late_fee_method||"fixed"} onChange={e=>setField("late_fee_method",e.target.value)} className="form-input">
                      <option value="fixed">Fixed</option>
                      <option value="percent">Percent</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Grace period (days)</label>
                    <input type="number" min={0} value={editData.grace_period_days||0} onChange={e=>setField("grace_period_days",Number(e.target.value))} className="form-input"/>
                  </div>
                </div>
                <div>
                  <label className="form-label">Rules text</label>
                  <textarea rows={3} value={editData.rules_text||""} onChange={e=>setField("rules_text",e.target.value)} className="form-input"/>
                </div>
                <div>
                  <label className="form-label">Payment account details</label>
                  <input value={editData.payment_account_details||""} onChange={e=>setField("payment_account_details",e.target.value)} className="form-input"/>
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer py-1">
                  <input type="checkbox" id="enable_comments" checked={!!editData.enable_comments} onChange={e=>setField("enable_comments",e.target.checked)} className="w-5 h-5"/>
                  <span className="text-sm">Enable member comments</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer py-1">
                  <input type="checkbox" id="allow_multiple_slots" checked={!!editData.allow_multiple_slots} onChange={e=>setField("allow_multiple_slots",e.target.checked)} className="w-5 h-5"/>
                  <span className="text-sm">Allow multiple slots per member <span className="font-normal text-xs" style={{color:"var(--muted)"}}>— lets the same person hold more than one payout position</span></span>
                </label>
              </div>

              <div className="card-tactile p-4 sm:p-6 space-y-4">
                <h3 className="font-display text-lg">WhatsApp</h3>
                <div>
                  <label className="form-label">Invite link</label>
                  <input value={editData.whatsapp_invite_link||""} onChange={e=>setField("whatsapp_invite_link",e.target.value)} className="form-input" placeholder="https://chat.whatsapp.com/..."/>
                </div>
                <div>
                  <label className="form-label">WhatsApp group name</label>
                  <input value={editData.whatsapp_group_name||""} onChange={e=>setField("whatsapp_group_name",e.target.value)} className="form-input"/>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <button onClick={saveGroup} disabled={saving} className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2">
                  <Save size={15}/>{saving ? "Saving…" : "Save changes"}
                </button>
                {saveMsg && <span className="text-sm" style={{color: saveMsg==="Saved!" ? "var(--primary)" : "#b91c1c"}}>{saveMsg}</span>}
              </div>
            </div>

            {/* ── Recalculate Dates card ── */}
            <div className="col-span-full">
              <div className="card-tactile p-4 sm:p-6 space-y-3" style={{borderLeft:"3px solid var(--primary)"}}>
                <div className="flex items-center gap-2">
                  <CalendarDays size={16} style={{color:"var(--primary)"}}/>
                  <h3 className="font-display text-base">Force-reset Cycle Due Dates</h3>
                </div>
                <p className="text-xs leading-relaxed" style={{color:"var(--muted)"}}>
                  Dates auto-update when you save start date, due day or frequency. Use this only if you need to
                  force-reset <em>pending</em> cycle dates to a specific start date without saving other settings.
                  Completed payouts are never touched.
                </p>
                <div className="rounded-lg px-4 py-3 text-xs space-y-1" style={{background:"var(--surface)"}}>
                  <div className="flex gap-6 flex-wrap">
                    <div><span style={{color:"var(--muted)"}}>Current start date: </span><strong>{group.start_date}</strong></div>
                    <div><span style={{color:"var(--muted)"}}>Due day: </span><strong>{group.due_day}</strong></div>
                    <div><span style={{color:"var(--muted)"}}>Frequency: </span><strong className="capitalize">{group.frequency}</strong></div>
                  </div>
                  <div style={{color:"var(--muted)"}}>
                    Will apply: start <strong>{editData.start_date||group.start_date}</strong>, day&nbsp;
                    <strong>{editData.due_day||group.due_day}</strong>
                    {(editData.start_date !== group.start_date || editData.due_day !== group.due_day) && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium" style={{background:"#fef3c7",color:"#92400e"}}>
                        unsaved change detected
                      </span>
                    )}
                  </div>
                </div>

                {!recalcConfirm ? (
                  <button onClick={()=>setRecalcConfirm(true)}
                    className="btn-secondary text-sm inline-flex items-center gap-2">
                    <RefreshCcw size={14}/> Preview &amp; apply new schedule
                  </button>
                ) : (
                  <div className="rounded-lg p-3 space-y-3" style={{background:"#fef3c7",border:"1px solid #fde68a"}}>
                    <div className="flex items-start gap-2 text-sm">
                      <AlertTriangle size={15} style={{color:"#d97706",flexShrink:0,marginTop:1}}/>
                      <span style={{color:"#92400e"}}>
                        This will recalculate due dates for <strong>all pending cycles</strong> based on
                        start date <strong>{editData.start_date||group.start_date}</strong> and
                        due day <strong>{editData.due_day||group.due_day}</strong>.
                        Make sure you have saved your changes first.
                      </span>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={()=>setRecalcConfirm(false)} className="btn-secondary text-sm">Cancel</button>
                      <button onClick={runRecalculate} disabled={recalcBusy}
                        className="btn-primary text-sm inline-flex items-center gap-2">
                        {recalcBusy ? <><RefreshCcw size={13} className="animate-spin"/> Recalculating…</> : <><RefreshCcw size={13}/> Confirm &amp; recalculate</>}
                      </button>
                    </div>
                  </div>
                )}
                {recalcMsg && (
                  <p className="text-sm font-medium" style={{color: recalcMsg.includes("fail") || recalcMsg.includes("error") ? "#b91c1c" : "var(--primary)"}}>
                    {recalcMsg}
                  </p>
                )}
              </div>
            </div>

          </div>
        )}
      </main>

      {/* Add slot modal */}
      {addSlotTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={()=>setAddSlotTarget(null)}>
          <form onClick={e=>e.stopPropagation()} onSubmit={addSlot}
            className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-xl p-5 sm:p-6">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4 sm:hidden" />
            <h3 className="font-display text-lg mb-1">Add slot — {addSlotTarget.name}</h3>
            <p className="text-xs mb-4" style={{color:"var(--muted)"}}>Each extra slot gives this member an additional payout position in the group.</p>
            <label className="form-label">Payout position <span className="text-red-600">*</span></label>
            <input type="number" required min={1} value={addSlotPos} onChange={e=>setAddSlotPos(e.target.value)}
              className="form-input mb-3" autoFocus placeholder="e.g. 6" />
            {addSlotErr && <div className="text-red-700 text-sm mb-3 px-3 py-2 rounded" style={{background:"#fef2f2"}}>{addSlotErr}</div>}
            <div className="flex gap-3">
              <button type="button" onClick={()=>setAddSlotTarget(null)} disabled={addSlotBusy} className="btn-secondary flex-1 text-sm">Cancel</button>
              <button type="submit" disabled={addSlotBusy} className="btn-primary flex-1 text-sm">{addSlotBusy ? "Adding..." : "Add slot"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Remove member confirmation modal */}
      {removeTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-xl p-5 sm:p-6">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4 sm:hidden" />
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg">Remove member</h3>
              <button onClick={()=>setRemoveTarget(null)} className="p-1 opacity-60 hover:opacity-100"><X size={18}/></button>
            </div>
            <p className="text-sm mb-4" style={{color:"var(--muted)"}}>
              Remove <b>{removeTarget.name}</b> from this group? This cannot be undone.
            </p>
            <div className="mb-4">
              <label className="form-label">Reason <span className="font-normal">(optional — logged in audit trail)</span></label>
              <input value={removeReason} onChange={e=>setRemoveReason(e.target.value)}
                className="form-input" placeholder="e.g. Missed payments, violated group terms" />
            </div>
            <div className="flex gap-3">
              <button onClick={()=>setRemoveTarget(null)} disabled={removeBusy} className="btn-secondary flex-1 text-sm">Cancel</button>
              <button onClick={confirmRemove} disabled={removeBusy} className="flex-1 text-sm px-4 py-2.5 rounded-lg font-semibold text-white" style={{background:"#dc2626"}}>
                {removeBusy ? "Removing..." : "Remove member"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual ledger management modal */}
      {ledgerOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={()=>setLedgerOpen(false)}>
          <div onClick={e=>e.stopPropagation()} className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4 sm:hidden" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg">Manual Ledger Management</h3>
              <button onClick={()=>setLedgerOpen(false)} className="p-1 opacity-60 hover:opacity-100"><X size={18}/></button>
            </div>
            <p className="text-xs mb-4" style={{color:"var(--muted)"}}>
              Manually update payment status for this member. Use this only if automatic tracking is incorrect.
              All changes are logged in the audit trail.
            </p>
            {ledgerLoading ? (
              <div className="text-center py-8 text-sm" style={{color:"var(--muted)"}}>Loading ledger...</div>
            ) : (
              <div className="space-y-2">
                {ledgerData.length === 0 ? (
                  <div className="text-center py-8 text-sm" style={{color:"var(--muted)"}}>No ledger entries found</div>
                ) : (
                  ledgerData.map((entry, idx) => (
                    <div key={idx} className="border rounded-lg p-3 text-sm" style={{borderColor:"var(--border)"}}>
                      {ledgerEdit?.cycle_no === entry.cycle_no ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">Cycle {entry.cycle_no}</span>
                            <span className="text-xs" style={{color:"var(--muted)"}}>Due: {entry.due_date || "N/A"}</span>
                          </div>
                          <div>
                            <label className="block text-xs mb-1 font-semibold" style={{color:"var(--muted)"}}>Status</label>
                            <select 
                              value={ledgerEdit.status} 
                              onChange={e=>setLedgerEdit(prev=>({...prev, status: e.target.value}))}
                              className="w-full border rounded px-2 py-1.5 text-sm"
                            >
                              <option value="Not_Due">Not_Due</option>
                              <option value="Due">Due</option>
                              <option value="Overdue">Overdue</option>
                              <option value="Submitted">Submitted</option>
                              <option value="Paid">Paid</option>
                              <option value="Rejected">Rejected</option>
                              <option value="Payout_Completed">Payout_Completed</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs mb-1 font-semibold" style={{color:"var(--muted)"}}>Paid Amount (optional)</label>
                            <input 
                              type="number" 
                              value={ledgerEdit.paid_amount} 
                              onChange={e=>setLedgerEdit(prev=>({...prev, paid_amount: e.target.value}))}
                              className="w-full border rounded px-2 py-1.5 text-sm"
                              placeholder="e.g. 5000"
                            />
                          </div>
                          <div>
                            <label className="block text-xs mb-1 font-semibold" style={{color:"var(--muted)"}}>Note (optional)</label>
                            <input 
                              value={ledgerEdit.note} 
                              onChange={e=>setLedgerEdit(prev=>({...prev, note: e.target.value}))}
                              className="w-full border rounded px-2 py-1.5 text-sm"
                              placeholder="Reason for manual override"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={()=>setLedgerEdit(null)} disabled={ledgerSaving} className="btn-secondary text-sm flex-1">Cancel</button>
                            <button onClick={saveLedgerEdit} disabled={ledgerSaving} className="btn-primary text-sm flex-1">
                              {ledgerSaving ? "Saving..." : "Save"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold">Cycle {entry.cycle_no}</div>
                            <div className="text-xs" style={{color:"var(--muted)"}}>Due: {entry.due_date || "N/A"}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <StatusBadge status={entry.status} />
                            {entry.manual_override && (
                              <span className="text-xs px-2 py-0.5 rounded" style={{background:"#fef3c7",color:"#92400e"}}>Manual</span>
                            )}
                            <button onClick={()=>startLedgerEdit(entry)} className="p-1.5 rounded hover:bg-gray-100" title="Edit">
                              <Pencil size={14} style={{color:"var(--muted)"}}/>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reply to member message modal */}
      {replyTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={()=>setReplyTarget(null)}>
          <div onClick={e=>e.stopPropagation()} className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl p-5 sm:p-6">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4 sm:hidden" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg">Reply to {replyTarget.from_user_name}</h3>
              <button onClick={()=>setReplyTarget(null)} className="p-1 opacity-60 hover:opacity-100"><X size={18}/></button>
            </div>
            <div className="mb-4 p-3 rounded-lg text-sm" style={{background:"var(--surface)",borderColor:"var(--border)",border:"1px solid"}}>
              <div className="font-semibold mb-1">{replyTarget.subject}</div>
              <div className="text-xs" style={{color:"var(--muted)"}}>Original message from {replyTarget.from_user_name}</div>
            </div>
            <div className="mb-4">
              <label className="form-label">Your reply</label>
              <textarea 
                value={replyBody}
                onChange={e=>setReplyBody(e.target.value)}
                rows={4}
                className="form-input"
                placeholder="Type your reply here..."
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button onClick={()=>setReplyTarget(null)} disabled={replySending} className="btn-secondary flex-1 text-sm">Cancel</button>
              <button onClick={sendReply} disabled={replySending || !replyBody.trim()} className="btn-primary flex-1 text-sm">
                {replySending ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
